
import Vue from 'vue';
import Component from 'vue-class-component';
import moment from 'moment';
import { BoPanel } from 'bonomo';
import Datim from '@/components/shared/Datim.vue';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import AuditLogActionPill from '@/components/settings/auditLogs/AuditLogActionPill.vue';
import AuditLogAuthorFilter from '@/components/settings/auditLogs/AuditLogAuthorFilter.vue';
import AuditLogCharts, { IAuditLogChartSelection } from '@/components/settings/auditLogs/AuditLogCharts.vue';
import AuditLogDetailDialog from '@/components/settings/auditLogs/AuditLogDetailDialog.vue';
import { BetterDataTableHeader } from '@/model/betterVuetifyInterfaces';
import {
  AUDIT_LOG_ACTION_DELETE,
  AUDIT_LOG_ACTION_INSERT,
  AUDIT_LOG_ACTIONS,
  auditLogService,
  IAuditLogAuthor,
  IAuditLogEntry,
} from '@/services/auditLogService';

const FILTER_DEBOUNCE_MS = 350;
const DEFAULT_PER_PAGE = 25;
const DATE_FORMAT = 'YYYY-MM-DD';

const PERIOD_TODAY = 'today';
const PERIOD_LAST_7_DAYS = 'last7';
const PERIOD_LAST_30_DAYS = 'last30';
const PERIOD_CUSTOM = 'custom';

// Days back from today (inclusive) covered by each preset.
const PERIOD_DAYS: { [period: string]: number } = {
  [PERIOD_TODAY]: 0,
  [PERIOD_LAST_7_DAYS]: 6,
  [PERIOD_LAST_30_DAYS]: 29,
};

interface IAuditLogFilters {
  search: string | null;
  table_name: string | null;
  action: string | null;
  record_id: string | null;
  ip: string | null;
}

function emptyFilters(): IAuditLogFilters {
  return { search: null, table_name: null, action: null, record_id: null, ip: null };
}

@Component({
  name: 'AuditLogs',
  components: {
    BoPanel, Datim, PaginationBar, AuditLogActionPill, AuditLogAuthorFilter, AuditLogCharts, AuditLogDetailDialog,
  },
})
export default class AuditLogs extends Vue {
  protected loading: boolean = true;
  protected entries: IAuditLogEntry[] = [];
  protected tableNames: string[] = [];
  protected currentPage: number = 1;
  protected total: number = 0;
  protected selectedPerPage: number = DEFAULT_PER_PAGE;
  readonly perPageOptions: number[] = [25, 50, 100];
  readonly actions: string[] = AUDIT_LOG_ACTIONS;
  readonly periodPresets: Array<string | null> = [null, PERIOD_TODAY, PERIOD_LAST_7_DAYS, PERIOD_LAST_30_DAYS];
  readonly today: string = moment().format(DATE_FORMAT);

  protected filters: IAuditLogFilters = emptyFilters();
  protected author: IAuditLogAuthor | null = null;
  protected period: string | null = null;
  protected customRange: string[] = [];
  protected datePickerOpen: boolean = false;
  private filterTimer: any = null;

  protected detailOpen: boolean = false;
  protected selectedEntry: IAuditLogEntry | null = null;

  created() {
    auditLogService.tableNames().then(tables => this.tableNames = tables);
  }

  get lastPage(): number {
    return Math.max(1, Math.ceil(this.total / (this.selectedPerPage || DEFAULT_PER_PAGE)));
  }

  get activeFilterCount(): number {
    const f = this.filters;

    return [f.search, f.table_name, f.action, f.record_id, f.ip, this.author, this.period].filter(value => !!value).length;
  }

  // The picker returns the two dates in click order, not chronologically.
  get sortedCustomRange(): string[] {
    return this.customRange.slice().sort();
  }

  get customRangeLabel(): string {
    const range = this.sortedCustomRange;
    if (!range.length) {
      return '';
    }

    return range.length === 1 || range[0] === range[1] ? range[0] : range[0] + ' → ' + range[1];
  }

  get dateFrom(): string | null {
    if (this.period === PERIOD_CUSTOM) {
      return this.sortedCustomRange[0] || null;
    }
    if (this.period && PERIOD_DAYS[this.period] !== undefined) {
      return moment().subtract(PERIOD_DAYS[this.period], 'days').format(DATE_FORMAT);
    }

    return null;
  }

  get dateTo(): string | null {
    if (this.period === PERIOD_CUSTOM) {
      return this.sortedCustomRange[1] || this.sortedCustomRange[0] || null;
    }

    return this.period ? this.today : null;
  }

  // The chart day the table is showing, when the filters are exactly what a bar click sets.
  get chartSelection(): IAuditLogChartSelection | null {
    const range = this.sortedCustomRange;
    if (!this.filters.table_name || !this.filters.action || this.period !== PERIOD_CUSTOM || range.length !== 2 || range[0] !== range[1]) {
      return null;
    }

    return { table_name: this.filters.table_name, action: this.filters.action, date: range[0] };
  }

  get headers(): BetterDataTableHeader[] {
    return [
      { text: this.$t('auditLogs.fields.date') as string, value: 'created_at', sortable: false },
      { text: this.$t('auditLogs.fields.record') as string, value: 'table_name', sortable: false },
      { text: this.$t('auditLogs.fields.action') as string, value: 'action', sortable: false },
      { text: this.$t('auditLogs.fields.changedBy') as string, value: 'user_id', sortable: false },
      { text: this.$t('auditLogs.fields.ip') as string, value: 'ip', sortable: false },
    ];
  }

  chipColor(color: string, active: boolean): string {
    return color + (active ? ' darken-1' : ' lighten-5');
  }

  chipTextColor(color: string, active: boolean): string {
    return active ? 'white' : color + ' darken-3';
  }

  actionColor(action: string): string {
    if (action === AUDIT_LOG_ACTION_INSERT) {
      return 'green';
    }

    return action === AUDIT_LOG_ACTION_DELETE ? 'red' : 'blue';
  }

  dayOf(date: string | null): string {
    return date ? moment(date).format('DD/MM/YY') : '';
  }

  timeOf(date: string | null): string {
    return date ? moment(date).format('HH:mm:ss') : '';
  }


  async loadData() {
    await this.fetchPage(this.currentPage);
  }

  async fetchPage(page: number) {
    this.loading = true;
    try {
      const result = await auditLogService.list({
        ...this.filters,
        page,
        perPage: this.selectedPerPage,
        user_id: this.author ? String(this.author.id) : null,
        date_from: this.dateFrom,
        date_to: this.dateTo,
      });
      this.entries = result.data;
      this.currentPage = result.page;
      this.total = result.total;
      this.selectedPerPage = result.per_page;
    } finally {
      this.loading = false;
    }
  }

  reload() {
    this.fetchPage(1);
  }

  reloadDebounced() {
    if (this.filterTimer) {
      clearTimeout(this.filterTimer);
    }
    this.filterTimer = setTimeout(() => this.reload(), FILTER_DEBOUNCE_MS);
  }

  // Clicking the active chip again unselects it, like a toggle.
  setTableName(table: string | null) {
    this.filters.table_name = table === this.filters.table_name ? null : table;
    this.reload();
  }

  filterByTable(table: string) {
    this.filters.table_name = table;
    this.reload();
  }

  setAction(action: string | null) {
    this.filters.action = action === this.filters.action ? null : action;
    this.reload();
  }

  setPeriod(period: string | null) {
    this.period = period === this.period ? null : period;
    this.customRange = [];
    this.reload();
  }

  // Clicking a bar lists that day's rows; clicking the same bar again removes those filters.
  onChartSelect(selection: IAuditLogChartSelection) {
    const current = this.chartSelection;
    const isSame = !!current && current.table_name === selection.table_name
      && current.action === selection.action && current.date === selection.date;

    this.filters.table_name = isSame ? null : selection.table_name;
    this.filters.action = isSame ? null : selection.action;
    this.period = isSame ? null : PERIOD_CUSTOM;
    this.customRange = isSame ? [] : [selection.date, selection.date];
    this.reload();
  }

  onCustomRangePicked() {
    if (this.customRange.length === 2) {
      this.period = PERIOD_CUSTOM;
      this.datePickerOpen = false;
      this.reload();
    }
  }

  onFilterAuthor(entry: IAuditLogEntry) {
    if (!entry.user_id) {
      return;
    }
    this.author = {
      id: entry.user_id,
      first_name: entry.changed_by_name,
      last_name: null,
      email: entry.changed_by_email,
    };
    this.detailOpen = false;
    this.reload();
  }

  // Clicking a top editor lists their changes; clicking the same one again removes that filter.
  onEditorSelect(author: IAuditLogAuthor) {
    this.author = this.author && this.author.id === author.id ? null : author;
    this.reload();
  }

  onFilterRecord(entry: IAuditLogEntry) {
    this.filters.table_name = entry.table_name;
    this.filters.record_id = entry.record_id !== null ? String(entry.record_id) : null;
    this.detailOpen = false;
    this.reload();
  }

  clearFilters() {
    this.filters = emptyFilters();
    this.author = null;
    this.period = null;
    this.customRange = [];
    this.reload();
  }

  onPerPageChange(perPage: number) {
    this.selectedPerPage = perPage;
    this.fetchPage(1);
  }

  openDetail(item: IAuditLogEntry) {
    this.selectedEntry = item;
    this.detailOpen = true;
  }
}
